// third-party
import { FormattedMessage } from 'react-intl'
import { Portfolio } from 'assets/images/icons'

// assets
import {
  BorderOutlined,
  BoxPlotOutlined,
  ChromeOutlined,
  DeploymentUnitOutlined,
  GatewayOutlined,
  MenuUnfoldOutlined,
  QuestionOutlined,
  SmileOutlined,
  StopOutlined,
  DashboardOutlined,
  SettingOutlined,
  InfoCircleOutlined,
  WalletOutlined,
  SwapOutlined,
  LinkOutlined,
  LineChartOutlined,
  DollarOutlined,
  BookOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
} from '@ant-design/icons'
import { useMemo } from 'react'
import useAuth from 'hooks/useAuth'
import usePermissions from 'hooks/usePermissions'
import { permissionsByName } from 'merklebase-utils/commons'
import RequestPageOutlinedIcon from '@mui/icons-material/RequestPageOutlined'

// icons
const icons = {
  ChromeOutlined,
  MenuUnfoldOutlined,
  BoxPlotOutlined,
  StopOutlined,
  BorderOutlined,
  SmileOutlined,
  GatewayOutlined,
  QuestionOutlined,
  DeploymentUnitOutlined,
  DashboardOutlined,
  SettingOutlined,
  InfoCircleOutlined,
  SwapOutlined,
  WalletOutlined,
  LinkOutlined,
  Portfolio,
  DollarOutlined,
  LineChartOutlined,
  BookOutlined,
  RequestPageOutlinedIcon,
  CheckCircleOutlined,
  CalendarOutlined,
}

// ==============================|| MENU ITEMS ||============================== //

const useMenu = () => {
  const { user } = useAuth()
  const featureFlags = useMemo(() => user?.featureFlags, [user])
  const isAdminOrOwner = useMemo(() => user?.isAdmin || user?.isOwner, [user])
  const { hasPermissionAccess, isRolesFeatureEnabled, isUsersOnlyFeatureEnabled } = usePermissions()

  const hasVenueDataPermission = useMemo(
    () => !isRolesFeatureEnabled || hasPermissionAccess(permissionsByName.venue_data?.id),
    [isRolesFeatureEnabled, hasPermissionAccess]
  )

  const hasDefiPermission = useMemo(
    () => !isRolesFeatureEnabled || hasPermissionAccess(permissionsByName.defi?.id),
    [isRolesFeatureEnabled, hasPermissionAccess]
  )

  const hasLoansPermission = useMemo(
    () => !isRolesFeatureEnabled || hasPermissionAccess(permissionsByName.loans?.id),
    [isRolesFeatureEnabled, hasPermissionAccess]
  )

  const hasOperationsPermission = useMemo(
    () => !isRolesFeatureEnabled || hasPermissionAccess(permissionsByName.operations?.id),
    [isRolesFeatureEnabled, hasPermissionAccess]
  )

  const hasPositionsPermission = useMemo(
    () => !isRolesFeatureEnabled || hasPermissionAccess(permissionsByName.positions?.id),
    [isRolesFeatureEnabled, hasPermissionAccess]
  )

  const isMerklebaseOrg = useMemo(() => {
    const org = user?.organizationName || ''
    return org === 'Annamite' || org.includes('merklebase')
  }, [user?.organizationName])

  const menuItems = useMemo(() => {
    const items = {
      id: 'mainMenu',
      type: 'group',
      children: [
        {
          id: 'dashboard',
          title: <FormattedMessage id="dashboard" />,
          type: 'item',
          url: '/dashboard',
          icon: icons.DashboardOutlined,
        },
        ...(featureFlags?.includes('events')
          ? [
              {
                id: 'tasks',
                title: <FormattedMessage id="tasks" />,
                type: 'collapse',
                breadcrumbs: true,
                icon: icons.CalendarOutlined,
                children: [
                  {
                    id: 'live-activity',
                    title: <FormattedMessage id="live-activity" />,
                    type: 'item',
                    url: '/tasks/live-activity',
                  },
                  {
                    id: 'configurations',
                    title: <FormattedMessage id="configurations" />,
                    type: 'item',
                    url: isAdminOrOwner
                      ? '/tasks/configurations/alerts'
                      : '/tasks/configurations/events',
                  },
                ],
              },
            ]
          : []),
        ...(hasVenueDataPermission
          ? [
              {
                id: 'balances',
                title: <FormattedMessage id="venue-snapshot" />,
                type: 'collapse',
                breadcrumbs: true,
                icon: icons.WalletOutlined,
                children: [
                  {
                    id: 'balances',
                    title: <FormattedMessage id="actual-balances" />,
                    type: 'item',
                    url: '/balances/balances',
                  },
                  {
                    id: 'margins',
                    title: <FormattedMessage id="margins" />,
                    type: 'item',
                    url: '/balances/margins',
                  },
                ],
              },
            ]
          : []),
        ...(featureFlags?.includes('defi') && hasDefiPermission
          ? [
              {
                id: 'defi',
                title: <FormattedMessage id="defi" />,
                type: 'collapse',
                breadcrumbs: true,
                icon: icons.LinkOutlined,
                children: [
                  {
                    id: 'balances',
                    title: <FormattedMessage id="balances" />,
                    type: 'item',
                    url: '/defi/balances',
                  },
                  {
                    id: 'subscription-redemption',
                    title: <FormattedMessage id="subscription-redemption" />,
                    type: 'item',
                    url: '/defi/subscription-redemption',
                  },
                  {
                    id: 'defi-performance',
                    title: <FormattedMessage id="performance" />,
                    type: 'item',
                    url: '/defi/performance',
                  },
                ],
              },
            ]
          : []),
        ...(isMerklebaseOrg && hasOperationsPermission
          ? [
              {
                id: 'reports',
                title: <FormattedMessage id="reports" />,
                type: 'item',
                url: '/reports',
                icon: icons.BookOutlined,
              },
            ]
          : []),
        ...(hasPositionsPermission
          ? [
              {
                id: 'risk',
                title: <FormattedMessage id="risk" />,
                type: 'collapse',
                icon: icons.InfoCircleOutlined,
                breadcrumbs: true,
                children: [
                  {
                    id: 'risk',
                    title: <FormattedMessage id="risk" />,
                    type: 'item',
                    url: '/risk/risk',
                  },
                  {
                    id: 'stress-test',
                    title: <FormattedMessage id="stress-test" />,
                    type: 'item',
                    url: '/risk/stress-test',
                  },
                ],
              },
            ]
          : []),
        ...(hasPositionsPermission
          ? [
              {
                id: 'performance',
                title: <FormattedMessage id="performance" />,
                type: 'collapse',
                icon: icons.LineChartOutlined,
                breadcrumbs: true,
                children: [
                  {
                    id: 'pnl-breakdown',
                    title: <FormattedMessage id="live-pnl" />,
                    type: 'item',
                    url: '/performance/pnl-breakdown',
                  },
                  // {
                  //   id: 'performance-analysis',
                  //   title: <FormattedMessage id="performance-analysis" />,
                  //   type: 'item',
                  //   url: '/performance/performance-analysis',
                  // },
                  {
                    id: 'history-pnl',
                    title: <FormattedMessage id="history-pnl" />,
                    type: 'item',
                    url: '/performance/history-pnl',
                  },
                ],
              },
            ]
          : []),
        ...(hasOperationsPermission
          ? [
              {
                id: 'operations',
                title: <FormattedMessage id="operations" />,
                type: 'collapse',
                icon: icons.SwapOutlined,
                breadcrumbs: true,
                children: [
                  {
                    id: 'transactions',
                    title: <FormattedMessage id="transactions" />,
                    type: 'item',
                    url: '/operations/transactions',
                  },
                  {
                    id: 'reconciliation',
                    title: <FormattedMessage id="reconciliation" />,
                    type: 'item',
                    url: '/operations/reconciliation',
                  },
                  ...(featureFlags?.includes('settlement')
                    ? [
                        {
                          id: 'settlement',
                          title: <FormattedMessage id="settlement" />,
                          type: 'item',
                          url: '/operations/settlement',
                        },
                      ]
                    : []),
                ],
              },
            ]
          : []),
        ...(featureFlags?.includes('loans') && hasLoansPermission
          ? [
              {
                id: 'lms',
                title: <FormattedMessage id="lms" />,
                type: 'collapse',
                breadcrumbs: true,
                icon: icons.RequestPageOutlinedIcon,
                children: [
                  {
                    id: 'credit-agreements',
                    title: <FormattedMessage id="credit-agreements" />,
                    type: 'item',
                    url: '/lms/credit-agreements',
                  },
                  {
                    id: 'tranches',
                    title: <FormattedMessage id="tranches" />,
                    type: 'item',
                    url: '/lms/tranches',
                  },
                ],
              },
            ]
          : []),
        ...(featureFlags?.includes('reports-new') && hasOperationsPermission
          ? [
              {
                id: 'reports-new',
                title: <FormattedMessage id="reports" />,
                type: 'item',
                breadcrumbs: true,
                icon: icons.BookOutlined,
                url: '/reports-new',
              },
            ]
          : []),
        {
          id: 'prices',
          title: <FormattedMessage id="prices" />,
          type: 'collapse',
          breadcrumbs: true,
          icon: icons.DollarOutlined,
          children: [
            {
              id: 'prices',
              title: <FormattedMessage id="currencies" />,
              type: 'item',
              url: '/prices/prices',
            },
            {
              id: 'derivatives-prices',
              title: <FormattedMessage id="derivatives-prices" />,
              type: 'item',
              url: '/prices/derivatives-prices',
            },
            {
              id: 'funding-rates',
              title: <FormattedMessage id="funding-rates" />,
              type: 'item',
              url: '/prices/funding-rates',
            },
          ],
        },
        {
          id: 'settings',
          title: <FormattedMessage id="settings" />,
          type: 'collapse',
          breadcrumbs: true,
          icon: icons.SettingOutlined,
          children: [
            {
              id: 'portfolios',
              title: <FormattedMessage id="portfolios" />,
              type: 'item',
              url: '/settings/portfolios',
            },
            ...(!isRolesFeatureEnabled || isAdminOrOwner
              ? [
                  {
                    id: 'link-management',
                    title: <FormattedMessage id="link-management" />,
                    type: 'item',
                    url: '/settings/link-management',
                  },
                ]
              : []),
            ...((isRolesFeatureEnabled && isAdminOrOwner) || isUsersOnlyFeatureEnabled
              ? [
                  {
                    id: 'user-permissions',
                    title: <FormattedMessage id="user-permissions" />,
                    type: 'item',
                    url: '/settings/user-permissions',
                  },
                ]
              : []),
            ...(!isRolesFeatureEnabled || isAdminOrOwner
              ? [
                  {
                    id: 'configurations',
                    title: <FormattedMessage id="configurations" />,
                    type: 'item',
                    url: '/settings/configurations',
                  },
                ]
              : []),
            ...(hasOperationsPermission
              ? [
                  {
                    id: 'historicalaum',
                    title: <FormattedMessage id="historicalaum" />,
                    type: 'item',
                    url: '/settings/historicalaum',
                  },
                ]
              : []),
            ...(!isRolesFeatureEnabled || isAdminOrOwner
              ? [
                  {
                    id: 'liquidity-tiers',
                    title: <FormattedMessage id="liquidity-tiers" />,
                    type: 'item',
                    url: '/settings/liquidity-tiers',
                  },
                ]
              : []),
          ],
        },
      ],
    }

    return items
  }, [
    featureFlags,
    hasVenueDataPermission,
    hasDefiPermission,
    isMerklebaseOrg,
    hasOperationsPermission,
    hasPositionsPermission,
    hasLoansPermission,
    isRolesFeatureEnabled,
    isAdminOrOwner,
    isUsersOnlyFeatureEnabled,
  ])

  return { menu: { items: [menuItems] } }
}

export default useMenu
